import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../api';

// Adjust this anchor date to match your actual pay period start.
// Every 14 days from this date is a new pay period.
const PAY_PERIOD_ANCHOR = new Date('2026-01-05T00:00:00');

function getPayPeriod(offset) {
  const today = new Date();
  const daysSinceAnchor = Math.floor((today - PAY_PERIOD_ANCHOR) / 86400000);
  const currentIndex = Math.floor(daysSinceAnchor / 14);
  const index = currentIndex + offset;
  const start = new Date(PAY_PERIOD_ANCHOR);
  start.setDate(start.getDate() + index * 14);
  const end = new Date(start);
  end.setDate(end.getDate() + 13);
  return { start, end };
}

function toDateStr(date) {
  return date.toISOString().split('T')[0];
}

function fmtHours(seconds) {
  if (!seconds || seconds <= 0) return null;
  return (seconds / 3600).toFixed(2);
}

const thStyle = {
  padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 12,
  textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray2)',
  borderBottom: '2px solid var(--gray5)', whiteSpace: 'nowrap', background: 'var(--gray6)',
};

const tdStyle = { padding: '8px 12px', whiteSpace: 'nowrap' };

export default function TimesheetPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = user.role === 'manager' || user.role === 'admin';

  const [periodOffset, setPeriodOffset] = useState(0);
  const [workers, setWorkers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(isManager ? '' : String(user.id));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const { start, end } = getPayPeriod(periodOffset);
  const startStr = toDateStr(start);
  const endStr = toDateStr(end);

  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });

  useEffect(() => {
    if (!isManager) return;
    api.get('/users').then(({ data }) => {
      setWorkers(data.filter(u => u.is_active));
    }).catch(() => {});
  }, [isManager]);

  const loadTimesheet = useCallback(async () => {
    if (isManager && !selectedUserId) { setRows([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ start: startStr, end: endStr });
      if (selectedUserId) params.set('user_id', selectedUserId);
      const { data } = await api.get(`/time-logs/timesheet?${params}`);
      setRows(data);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [startStr, endStr, selectedUserId, isManager]);

  useEffect(() => { loadTimesheet(); }, [loadTimesheet]);

  // Build pivot: key = "job_number::assembly_name"
  const keys = [];
  const pivot = {};
  const dayTotals = {};
  let grandTotal = 0;

  for (const row of rows) {
    const key = `${row.job_number}::${row.assembly_name}`;
    if (!pivot[key]) {
      pivot[key] = { job_number: row.job_number, job_title: row.job_title, assembly_name: row.assembly_name, dates: {}, total: 0 };
      keys.push(key);
    }
    const ds = typeof row.work_date === 'string' ? row.work_date.split('T')[0] : toDateStr(new Date(row.work_date));
    const secs = Number(row.total_seconds);
    pivot[key].dates[ds] = (pivot[key].dates[ds] || 0) + secs;
    pivot[key].total += secs;
    dayTotals[ds] = (dayTotals[ds] || 0) + secs;
    grandTotal += secs;
  }

  const periodLabel = `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="page">
      <div className="topbar">
        <button className="btn btn-ghost" style={{ padding: '6px 12px', fontSize: 13 }} onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <h1>Timesheet</h1>
        <div />
      </div>

      <div className="content" style={{ maxWidth: 'none' }}>
        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {isManager && (
            <select
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid var(--gray5)', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 180 }}
            >
              <option value="">Select worker…</option>
              {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost" style={{ padding: '6px 14px' }} onClick={() => setPeriodOffset(o => o - 1)}>←</button>
            <span style={{ fontSize: 15, fontWeight: 600, minWidth: 210, textAlign: 'center' }}>{periodLabel}</span>
            <button className="btn btn-ghost" style={{ padding: '6px 14px' }} onClick={() => setPeriodOffset(o => o + 1)} disabled={periodOffset >= 0}>→</button>
          </div>

          {!isManager && (
            <span style={{ fontSize: 14, color: 'var(--gray3)' }}>{user.name}</span>
          )}
        </div>

        {loading && <div style={{ color: 'var(--gray3)', fontSize: 14 }}>Loading…</div>}

        {!loading && isManager && !selectedUserId && (
          <div style={{ color: 'var(--gray3)', fontSize: 14 }}>Select a worker to view their timesheet.</div>
        )}

        {!loading && (selectedUserId || !isManager) && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, minWidth: 110 }}>Job</th>
                  <th style={{ ...thStyle, minWidth: 140 }}>Assembly</th>
                  {days.map(d => (
                    <th key={toDateStr(d)} style={{ ...thStyle, minWidth: 72, textAlign: 'right' }}>
                      {d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
                    </th>
                  ))}
                  <th style={{ ...thStyle, minWidth: 72, textAlign: 'right', borderLeft: '2px solid var(--gray4)' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 ? (
                  <tr>
                    <td colSpan={17} style={{ padding: '32px', textAlign: 'center', color: 'var(--gray3)' }}>
                      No time logged this pay period.
                    </td>
                  </tr>
                ) : keys.map(key => {
                  const item = pivot[key];
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid var(--gray5)' }}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 600 }}>{item.job_number}</span>
                        {item.job_title && <div style={{ fontSize: 12, color: 'var(--gray3)' }}>{item.job_title}</div>}
                      </td>
                      <td style={tdStyle}>{item.assembly_name}</td>
                      {days.map(d => {
                        const ds = toDateStr(d);
                        const h = fmtHours(item.dates[ds]);
                        return (
                          <td key={ds} style={{ ...tdStyle, textAlign: 'right', color: h ? 'inherit' : 'var(--gray5)' }}>
                            {h || '—'}
                          </td>
                        );
                      })}
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, borderLeft: '2px solid var(--gray4)', background: 'var(--gray6)' }}>
                        {fmtHours(item.total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {keys.length > 0 && (
                <tfoot>
                  <tr style={{ background: 'var(--gray1)', color: '#fff', fontWeight: 700 }}>
                    <td style={tdStyle} colSpan={2}>Total</td>
                    {days.map(d => {
                      const ds = toDateStr(d);
                      const h = fmtHours(dayTotals[ds]);
                      return (
                        <td key={ds} style={{ ...tdStyle, textAlign: 'right' }}>{h || '—'}</td>
                      );
                    })}
                    <td style={{ ...tdStyle, textAlign: 'right', borderLeft: '2px solid var(--gray4)' }}>
                      {fmtHours(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>

            {grandTotal > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray3)' }}>
                Hours in decimal format — e.g. 1.50 = 1h 30m · Total: {fmtHours(grandTotal)} hrs
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
